import React, { useState, useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import FormInput from './FormInput'
import FormInputTextarea from './FormInputTextarea'

const CONTACT_API = '/api/contact'
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY

const ContactUsForm = () => {
  const captchaRef = useRef(null)

  const [values, setValues] = useState({
    name: '',
    email: '',
    telephone: '',
    message: '',
  })

  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [captchaError, setCaptchaError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const inputs = [
    {
      id: 1,
      name: 'name',
      type: 'text',
      placeholder: 'Your full name',
      errorMessage: 'Please add a name.',
      label: 'Name',
      required: true,
    },
    {
      id: 2,
      name: 'email',
      type: 'email',
      placeholder: 'your.email@example.com',
      errorMessage: 'Please add a valid email address.',
      label: 'Email',
      required: true,
    },
    {
      id: 3,
      name: 'telephone',
      type: 'text',
      placeholder: 'Phone number (optional)',
      errorMessage: 'Unknown input, please check your number.',
      label: 'Telephone',
    },
    {
      id: 4,
      name: 'message',
      type: 'textarea',
      placeholder: "Tell us about your project or inquiry...",
      errorMessage: "Please include a message about what you're interested in.",
      label: 'Message',
      required: true,
    },
  ]

  const validateField = (name, value) => {
    const input = inputs.find((item) => item.name === name)
    if (input && input.required && (!value || value.trim() === '')) {
      return input.errorMessage
    }
    if (name === 'email' && value && !/\S+@\S+\.\S+/.test(value)) {
      return input.errorMessage
    }
    return ''
  }

  const resetCaptcha = () => {
    captchaRef.current?.reset()
    setCaptchaError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError('')

    const newErrors = {}
    inputs.forEach((input) => {
      const error = validateField(input.name, values[input.name])
      if (error) {
        newErrors[input.name] = error
      }
    })

    setErrors(newErrors)
    setTouched(inputs.reduce((acc, input) => ({ ...acc, [input.name]: true }), {}))

    if (Object.keys(newErrors).length > 0) {
      return
    }

    if (!RECAPTCHA_SITE_KEY) {
      setSubmitError('Contact form is not configured. Please call us instead.')
      return
    }

    const recaptchaToken = captchaRef.current?.getValue()
    if (!recaptchaToken) {
      setCaptchaError('Please complete the reCAPTCHA verification.')
      return
    }
    setCaptchaError('')

    setSubmitting(true)
    try {
      const response = await fetch(CONTACT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, recaptchaToken }),
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.error || 'Request failed')
      }

      alert('Thank you! We will contact you in 1-2 business days.')
      setValues({ name: '', email: '', telephone: '', message: '' })
      setErrors({})
      setTouched({})
      resetCaptcha()
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong sending your message. Please try again or call us.')
      resetCaptcha()
    } finally {
      setSubmitting(false)
    }
  }

  const onChange = (e) => {
    const { name, value } = e.target
    setValues({ ...values, [name]: value })

    if (touched[name]) {
      const error = validateField(name, value)
      setErrors({ ...errors, [name]: error })
    }
  }

  const onBlur = (e) => {
    const { name, value } = e.target
    setTouched({ ...touched, [name]: true })
    const error = validateField(name, value)
    setErrors({ ...errors, [name]: error })
  }

  return (
    <form className="contact-form" id="form" onSubmit={handleSubmit}>
      {inputs.map((input) => (
        <div key={input.id} className="mb-3">
          {input.type === 'textarea' ? (
            <FormInputTextarea
              {...input}
              value={values[input.name]}
              onChange={onChange}
              onBlur={onBlur}
              error={touched[input.name] ? errors[input.name] : ''}
            />
          ) : (
            <FormInput
              {...input}
              value={values[input.name]}
              onChange={onChange}
              onBlur={onBlur}
              error={touched[input.name] ? errors[input.name] : ''}
            />
          )}
        </div>
      ))}

      <div className="pfc-recaptcha-wrap">
        {RECAPTCHA_SITE_KEY ? (
          <>
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={() => setCaptchaError('')}
              onExpired={() => setCaptchaError('reCAPTCHA expired. Please verify again.')}
            />
            {captchaError && <p className="form-error">{captchaError}</p>}
          </>
        ) : (
          <p className="form-error">
            reCAPTCHA is not configured. Set REACT_APP_RECAPTCHA_SITE_KEY and restart the app.
          </p>
        )}
      </div>

      {submitError && <p className="form-error" style={{ marginBottom: '1rem' }}>{submitError}</p>}

      <button type="submit" className="btn btn-primary w-100 mt-4" disabled={submitting || !RECAPTCHA_SITE_KEY}>
        {submitting ? 'Sending…' : 'Send Message'}
      </button>
    </form>
  )
}

export default ContactUsForm
